import { isPlatformBrowser, NgClass } from '@angular/common';
import {
	AfterViewInit,
	ChangeDetectionStrategy,
	Component,
	effect,
	HostListener,
	inject,
	input,
	model,
	OnDestroy,
	PLATFORM_ID,
	Signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';

import { TranslatePipe } from '@ngx-translate/core';
import { createPopper, Instance, Placement } from '@popperjs/core';
import { SvgIconComponent } from 'angular-svg-icon';

import { cn } from '@valk-nx/core/lib/core';
import { ClickOutsideDirective } from '@valk-nx/core/lib/directives/click-outside/click-outside.directive';
import { debounceFn } from '@valk-nx/helpers/lib/debounce/debounce.helper';
import { GeneralHelper } from '@valk-nx/helpers/lib/general/general.helper';
import { ViewPortService } from '@valk-nx/services/viewport/src/lib/viewport.service';

@Component({
	changeDetection: ChangeDetectionStrategy.OnPush,
	selector: 'vp-tooltip',
	templateUrl: './tooltip.component.html',
	standalone: true,
	imports: [NgClass, TranslatePipe, SvgIconComponent, ClickOutsideDirective],
	styleUrls: ['./tooltip.component.scss'],
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
	private readonly platformId = inject(PLATFORM_ID);
	private readonly viewport = inject(ViewPortService);

	alignment = input<Placement>('top');
	showTooltip = model<boolean>(false);
	iconSize = input('w-4', {
		transform: (value: string | undefined) => value || 'w-4',
	});
	tooltipColor = input('text-dark-primary', {
		transform: (value: string | undefined) => value || 'text-dark-primary',
	});
	ariaLabel = input<string>('');
	fixedHeight = input<boolean>(true);

	isMobile: Signal<boolean | undefined>;

	uuid = GeneralHelper.generateRandomId();
	tooltipContentId = 'tooltip-content-' + this.uuid;
	tooltipTriggerId = 'tooltip-trigger-' + this.uuid;
	tooltipArrowId = 'tooltip-arrow-' + this.uuid;

	popperInstance!: Instance;
	popoverInstance!: HTMLElement;
	triggerInstance!: Element;
	arrowInstance!: HTMLElement;
	scrollPosition = 0;

	cn = cn;

	private readonly debouncedResizeTooltip = debounceFn(
		() => this.resizeTooltip(),
		300,
	);

	constructor() {
		this.isMobile = toSignal(this.viewport.isMobile$);

		effect(() => {
			const _alignment = this.alignment();
			this.updateTooltip();
		});
	}

	ngAfterViewInit(): void {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId)) {
			/* v8 ignore stop */
			this.popoverInstance = document.getElementById(
				this.tooltipContentId,
			) as HTMLElement;
			this.triggerInstance = document.getElementById(
				this.tooltipTriggerId,
			) as Element;
			this.arrowInstance = document.getElementById(
				this.tooltipArrowId,
			) as HTMLElement;

			this.popperInstance = createPopper(
				this.triggerInstance,
				this.popoverInstance,
				{
					modifiers: [
						{
							name: 'offset',
							options: {
								offset: [0, 8],
							},
						},
						{
							name: 'arrow',
							options: {
								element: this.arrowInstance,
								padding: 12,
							},
						},
						{ enabled: true, name: 'flip' },
					],
					placement: this.alignment(),
				},
			);
		}
	}

	ngOnDestroy() {
		/* v8 ignore start */
		if (isPlatformBrowser(this.platformId) && this.popperInstance) {
			this.popperInstance.destroy();
		}
		/* v8 ignore stop */
	}

	@HostListener('window:resize')
	onResize() {
		this.debouncedResizeTooltip();
	}

	resizeTooltip() {
		if (this.showTooltip()) {
			this.hide();
		}
		this.updateTooltip();
	}

	updateTooltip() {
		if (!this.popperInstance) {
			return;
		}
		this.popperInstance.setOptions({
			placement: this.alignment(),
		});
		this.popperInstance.forceUpdate();
	}

	show() {
		this.updateTooltip();
		this.showTooltip.set(true);
		this.scrollPosition = window.scrollY;
		if (this.isMobile()) {
			GeneralHelper.mobileBodyLock(true, this.scrollPosition, true);
		}
	}

	hide() {
		this.showTooltip.set(false);
		GeneralHelper.mobileBodyLock(false, this.scrollPosition, true);
	}

	toggle() {
		if (this.showTooltip()) {
			this.hide();
		} else {
			this.show();
		}
	}

	hideAndStopPropagation(event: Event) {
		event.stopPropagation();
		event.preventDefault();
		this.hide();
	}
}
